import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { authApi } from '../api/auth';
import type { PermissionKey } from '../api/permissionKeys';

const PERMS_KEY = ['auth', 'permissions'] as const;

/**
 * usePermissions — the #14 frontend authorisation hook.
 *
 * Fetches the current user's EFFECTIVE permission set from
 * GET /auth/me/permissions (group defaults + per-user overrides, resolved
 * server-side) and exposes `can(key)`. Affordances gate on `can(...)`
 * instead of `user.role === …` (design §5). Deny-by-default: `can` is false
 * until the set loads and false for any key not in the set, so a missing
 * permission never accidentally enables an action.
 *
 * Mirrors useCurrentUser: a 401 resolves to an empty set (unauthenticated is
 * a normal state, not an error). The backend remains the real gate; this only
 * decides what to show/enable.
 */
export function usePermissions() {
  const q = useQuery<string[], ApiError>({
    queryKey: PERMS_KEY,
    queryFn: async () => {
      try {
        return (await authApi.permissions()).permissions;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          return [];
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });

  const set = useMemo(() => new Set(q.data ?? []), [q.data]);
  const can = useCallback((key: PermissionKey) => set.has(key), [set]);

  return {
    can,
    permissions: q.data ?? [],
    isLoading: q.isLoading,
  };
}
